import { useState } from "react";

import { useRecentFilings, useUndoFilingTarget } from "../hooks/useFilings";
import type { FilingTargetStatus } from "../api/types";

const STATUS_STYLES: Record<FilingTargetStatus, string> = {
  pending: "bg-slate-100 text-slate-600",
  filed: "bg-green-100 text-green-800",
  partial: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  undone: "bg-slate-100 text-slate-500 line-through",
};

export function RecentFilings() {
  const recentQuery = useRecentFilings();
  const undoMutation = useUndoFilingTarget();
  const [confirmingId, setConfirmingId] = useState<number | null>(null);

  if (recentQuery.isLoading) {
    return <p className="text-sm text-slate-400">Loading recent filings…</p>;
  }

  if (recentQuery.isError) {
    return (
      <p role="alert" className="text-sm text-red-600">
        {recentQuery.error.message}
      </p>
    );
  }

  const filings = recentQuery.data?.filings ?? [];

  if (filings.length === 0) {
    return <p className="text-sm text-slate-400">No filings yet.</p>;
  }

  function handleUndo(targetId: number): void {
    undoMutation.mutate(targetId, {
      onSettled: () => setConfirmingId(null),
    });
  }

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {filings.map((filing) => (
          <li
            key={filing.target_id}
            className="rounded-md border border-slate-200 p-2"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">
                  {filing.subject}
                </p>
                <p className="text-xs text-slate-500">
                  {filing.project_name} ·{" "}
                  {new Date(filing.filed_at).toLocaleString()}
                </p>
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[filing.status]}`}
              >
                {filing.status}
              </span>
            </div>
            {filing.can_undo && (
              <div className="mt-2">
                {confirmingId === filing.target_id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600">
                      Undo this filing?
                    </span>
                    <button
                      type="button"
                      onClick={() => handleUndo(filing.target_id)}
                      disabled={undoMutation.isPending}
                      className="rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {undoMutation.isPending ? "Undoing…" : "Confirm undo"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingId(null)}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingId(filing.target_id)}
                    className="text-xs font-medium text-red-700 hover:underline"
                  >
                    Undo
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {undoMutation.isError && (
        <p role="alert" className="text-xs text-red-600">
          {undoMutation.error.message}
        </p>
      )}
    </div>
  );
}
